import Vue from 'vue';
import _get from 'lodash.get';
import { actions, mutations } from './types';
import { ActionState } from '@/types/store/state';
import router from '@/router';
import api from '@/modules/api';
import { Credentials, PasswordChangeInfo, SinupCredentials, UserInfo, AuthToken } from '@/types/user-info';
import { Dictionary } from '@/types/base';
import { DeviceInfo } from '@/types/device-info';
import { LoginResult } from '@/types/store/actions';
import { resolveUserCountry } from '@/utils/countries';
import { getErrorMessage } from '@/utils/common';
import { defaultCurrency } from '@/constants/currency';

const login = async ({ state, commit }: ActionState, { username, password, twoFactorCode }: Credentials) => {
    // console.log('ACTION login', username, password);
    commit(mutations.LOADING, { loading: true });
    commit(mutations.SET_AUTH_TOKEN, { token: null });
    try {
        const { authToken: token, twoFactorCodeRequired } = await api.login(username, password, twoFactorCode);
        if (twoFactorCodeRequired) return LoginResult.LoginTwoFactorRequired;

        commit(mutations.SET_AUTH_TOKEN, { token });
        let { userDetails } = await api.loadUser();
        if (!userDetails.country) {
            const countryDetails = await resolveUserCountry();
            if (countryDetails) {
                const { updatedUser } = await api.updateUser({
                    country: countryDetails.value,
                    currency: countryDetails.isoCode ? countryDetails.isoCode : defaultCurrency,
                });
                userDetails = updatedUser as UserInfo;
            }
        }

        const subscriptions = await api.loadUserCurrentSubscriptions();
        commit(mutations.SET_USER_SUBSCRIPTIONS, { subscriptions });
        commit(mutations.SET_USER, { user: userDetails });
        commit(mutations.SET_DEVICES, { devices: [] });
        commit(mutations.SET_ALL_DEVICES, { allDevices: [] });

        const { device } = state;
        if (device && device.userId == userDetails._id) {
            router.push({ name: 'device-overview', params: { deviceUUID: device.deviceUUID as string } });
        } else {
            commit(mutations.SET_CURRENT_DEVICE, { device: null });
            commit(mutations.SET_SELECTED_DEVICE_TYPE, { selectedDeviceType: 'all' });
            router.push({ name: 'device-overview' });
        }
        return LoginResult.LoginSuccess;
    } catch (error) {
        commit(mutations.SET_AUTH_TOKEN, { token: null });
        commit(mutations.SET_USER, { user: null });

        const message = _get(error, 'response.data.message');
        if (message) {
            Vue.toasted.error(message);
            if (message.includes('verify')) {
                router.push({
                    name: 'verificationEmail-resend',
                    params: { email: username as string },
                });
                return;
            }
        } else {
            Vue.toasted.error(`Username or password incorrect`);
        }

        return LoginResult.LoginFailed;
    } finally {
        commit(mutations.LOADING, { loading: false });
    }
};

const signup = async ({ commit }: ActionState, { name, email, country, currency, password }: SinupCredentials) => {
    commit(mutations.LOADING, { loading: true });
    commit(mutations.SET_AUTH_TOKEN, { token: null });

    try {
        await api.signup(name, email, password, country ?? '', currency ?? '');
        // commit(mutations.SET_AUTH_TOKEN, { token });
        // commit(mutations.SET_USER, { user });
        Vue.toasted.success('Verification link has been sent to your Email. Please verify');
        router.push({ name: 'account-login' });
        // return LoginResult.LoginSuccess;
    } catch (error) {
        commit(mutations.SET_AUTH_TOKEN, { token: null });
        commit(mutations.SET_USER, { user: null });
        const message = _get(error, 'response.data.message');
        if (message) {
            Vue.toasted.error(message);
        }
        // return LoginResult.LoginFailed;
    } finally {
        commit(mutations.LOADING, { loading: false });
    }
};

const resetPassword = async ({ commit }: ActionState, { email }: { email: string }) => {
    commit(mutations.LOADING, { loading: true });
    try {
        await api.resetPassword(email);
        Vue.toasted.success('Verification link has been sent to your Email. Please verify');
    } catch (error) {
        const message = _get(error, 'response.data.message');
        if (message) {
            Vue.toasted.error(message);
        } else {
            Vue.toasted.error('Something went wrong, please try again');
        }
    } finally {
        commit(mutations.LOADING, { loading: false });
    }
};

const resendVerificationEmail = async ({ commit }: ActionState, { email }: { email: string }) => {
    commit(mutations.LOADING, { loading: true });
    try {
        await api.resendVerificationEmail(email);
        Vue.toasted.success('Email sent.');
    } catch (error) {
        const message = _get(error, 'response.data.message');
        if (message) {
            Vue.toasted.error(message);
        } else {
            Vue.toasted.error('Something went wrong, please try again');
        }
    } finally {
        commit(mutations.LOADING, { loading: false });
    }
};

const setNewPassword = async (
    { commit }: ActionState,
    { token, password, confirmation }: { token: string; password: string; confirmation: string },
) => {
    commit(mutations.LOADING, { loading: true });
    try {
        await api.setNewPassword(token, password, confirmation);
        Vue.toasted.success('Your password has been successfully changed');
        router.push({ name: 'account-login' });
    } catch (error) {
        const message = _get(error, 'response.data.message');
        if (message) Vue.toasted.error(message);
    } finally {
        commit(mutations.LOADING, { loading: false });
    }
};

const verifyEmail = async ({ commit }: ActionState, { token }: { token: string }) => {
    commit(mutations.LOADING, { loading: true });
    try {
        return await api.verifyEmail(token);
    } catch (error) {
        const message = _get(error, 'response.data.message');
        if (message) Vue.toasted.error(message);
    } finally {
        commit(mutations.LOADING, { loading: false });
    }
};

const logout = ({ commit }: ActionState) => {
    commit(mutations.SET_AUTH_TOKEN, { token: null });
    commit(mutations.SET_USER, { user: null });
    commit(mutations.SET_DEVICES, { devices: [] });
    commit(mutations.SET_ALL_DEVICES, { allDevices: [] });
    commit(mutations.SET_USER_SUBSCRIPTIONS, { subscriptions: [] });
    // commit(mutations.SET_CURRENT_DEVICE, { device: null });
    router.push({ name: 'account-login' });
};

const checkAuthToken = async (
    { state, commit }: ActionState,
    { next, params, sessionToken }: { next?: string; params?: Dictionary<string>; sessionToken?: AuthToken },
) => {
    if (!sessionToken && !state.token) return router.push({ name: 'login' });
    commit(mutations.LOADING, { loading: true, message: 'Authenticating...' });

    try {
        if (sessionToken) {
            commit(mutations.SET_AUTH_TOKEN, { token: sessionToken });
            commit(mutations.SET_SELECTED_DEVICE_TYPE, { selectedDeviceType: 'all' });

            const deviceUUID = params?.deviceUUID;
            if (deviceUUID) {
                const device = await api.loadDevice(deviceUUID);
                commit(mutations.SET_CURRENT_DEVICE, { device });
            }
        }

        const { token } = state;
        api.setAuthToken(token);
        const user = await api.loadCurrentUser();
        commit(mutations.SET_USER, { user });

        const subscriptions = await api.loadUserCurrentSubscriptions();
        commit(mutations.SET_USER_SUBSCRIPTIONS, { subscriptions });

        router.push({ name: next || 'device-overview', params: params || {} });
    } catch (error) {
        api.setAuthToken(null);
        commit(mutations.SET_AUTH_TOKEN, { token: null });
        commit(mutations.SET_USER, { user: null });
        Vue.toasted.error(getErrorMessage(error, 'Session expired, please login again'));
        return router.push({ name: 'login' });
    } finally {
        commit(mutations.LOADING, { loading: false });
    }
};

const loadDevices = async (
    { state, commit }: ActionState,
    { userId, message }: { userId: string; message: string | undefined },
) => {
    commit(mutations.LOADING, { loading: true, message });
    commit(mutations.SET_DEVICES_FETCHED, { devicesFetched: false });
    try {
        const devices = await api.loadDevices(
            userId,
            { page: 0, perPage: 999 },
            state.selectedDeviceType,
            state.searchName,
        );

        // set selected device
        const { device } = state;
        const index = devices.findIndex((currentdevice) => currentdevice._id === device?._id);
        if (devices.length == 0) {
            commit(mutations.SET_CURRENT_DEVICE, { device: null });
        } else if (device === null || index === -1) {
            commit(mutations.SET_CURRENT_DEVICE, { device: devices[0] });
            router.push({ name: 'device-overview', params: { deviceUUID: devices[0].deviceUUID as string } });
        }

        commit(mutations.SET_DEVICES, { devices });
        // commit(mutations.SET_ALL_DEVICES, { allDevices: devices });
    } catch (error) {
        Vue.toasted.error(getErrorMessage(error, 'Unable to load devices'));
    } finally {
        commit(mutations.LOADING, { loading: false });
        commit(mutations.SET_DEVICES_FETCHED, { devicesFetched: true });
    }
};

const loadGroups = async (
    { commit }: ActionState,
) => {
    commit(mutations.LOADING, { loading: true });
    try {
        const response = await api.getDeviceGroups({ page: 0, perPage: 999 });
        commit(mutations.SET_GROUPS, { groups: response.data });
    } catch (error) {
        Vue.toasted.error(getErrorMessage(error, 'Unable to load devices group'));
    } finally {
        commit(mutations.LOADING, { loading: false });
    }
};

const loadDevice = async (
    { state, commit }: ActionState,
    { deviceUUID, message }: { deviceUUID: string; message: string | undefined },
) => {
    if (!deviceUUID) return;
    commit(mutations.LOADING, { loading: true, message });
    try {
        const device = await api.loadDevice(deviceUUID);
        commit(mutations.SET_CURRENT_DEVICE, { device });
    } catch (error) {
        Vue.toasted.error(getErrorMessage(error, 'Unable to load device'));
    } finally {
        if (state.devicesFetched == true) commit(mutations.LOADING, { loading: false });
    }
};

const updateDevice = async (
    { commit, state }: ActionState,
    { device, message }: { device: Partial<DeviceInfo>; message: string | undefined },
): Promise<DeviceInfo | null> => {
    commit(mutations.LOADING, { loading: true, message });
    try {
        const updated = await api.updateDevice(device);

        // set selected device
        commit(mutations.SET_CURRENT_DEVICE, { device: updated });

        // set filtered devices
        const devices = [...state.devices];
        const index = devices.findIndex((device) => device._id === updated._id);
        devices.splice(index, 1, updated);
        commit(mutations.SET_DEVICES, { devices });

        // // set all devices
        // const allDevices = [...state.allDevices];
        // const AllDevicesIndex = allDevices.findIndex((device) => device._id === updated._id);
        // allDevices.splice(AllDevicesIndex, 1, updated);
        // commit(mutations.SET_ALL_DEVICES, { allDevices: allDevices });

        Vue.toasted.success(`Saved successfully`);
        return updated;
    } catch (error) {
        console.error(_get(error, 'response'));
        Vue.toasted.error(`Unable to update device: ${_get(error, 'response.data.message', 'internal error')}`);
    } finally {
        commit(mutations.LOADING, { loading: false });
    }

    return null;
};

const updatePassword = async ({ commit }: ActionState, passwordChange: PasswordChangeInfo): Promise<boolean> => {
    commit(mutations.LOADING, { loading: true });
    try {
        await api.updatePassword(passwordChange);
        Vue.toasted.success(`Password was successfully changed`);
        return true;
    } catch (error) {
        console.error(_get(error, 'response'));
        Vue.toasted.error(`Unable to set new password: ${_get(error, 'response.data.message', 'internal error')}`);
        return false;
    } finally {
        commit(mutations.LOADING, { loading: false });
    }
};

const updateEmail = async ({ commit }: ActionState, email: string): Promise<boolean> => {
    commit(mutations.LOADING, { loading: true });
    try {
        const { updatedUser } = await api.updateEmail(email);
        commit(mutations.SET_USER, { user: updatedUser });
        Vue.toasted.success(`Verification link has been sent to new email, please verify.`);
        return true;
    } catch (error) {
        console.error(_get(error, 'response'));
        Vue.toasted.error(`${_get(error, 'response.data.message', 'internal error')}`);
        return false;
    } finally {
        commit(mutations.LOADING, { loading: false });
    }
};

const updateUser = async ({ commit }: ActionState, { user }: { user: UserInfo }): Promise<boolean> => {
    commit(mutations.LOADING, { loading: true });
    try {
        const { updatedUser } = await api.updateUser(user);
        commit(mutations.SET_USER, { user: updatedUser });
        Vue.toasted.success(`Saved successfully`);
        return true;
    } catch (error) {
        console.error(_get(error, 'response'));
        Vue.toasted.error(`${_get(error, 'response.data.message', 'internal error')}`);
        return false;
    } finally {
        commit(mutations.LOADING, { loading: false });
    }
};

const pauseUserSubscription = async (
    { commit }: ActionState,
    { subscriptionId, resumeDate }: { subscriptionId: string; resumeDate: Date },
): Promise<boolean> => {
    commit(mutations.LOADING, { loading: true });
    try {
        await api.pauseSubscription(subscriptionId, resumeDate);
        const subscriptions = await api.loadUserCurrentSubscriptions();
        commit(mutations.SET_USER_SUBSCRIPTIONS, { subscriptions });
        Vue.toasted.success(`Paused successfully`);
        return true;
    } catch (error) {
        console.error(_get(error, 'response'));
        Vue.toasted.error(`Unable to pause subscription: ${_get(error, 'response.data.message', 'internal error')}`);
        return false;
    } finally {
        commit(mutations.LOADING, { loading: false });
    }
};

const resumeUserSubscription = async ({ commit }: ActionState, subscriptionId: string): Promise<boolean> => {
    commit(mutations.LOADING, { loading: true });
    try {
        await api.resumeSubscription(subscriptionId);
        const subscriptions = await api.loadUserCurrentSubscriptions();
        commit(mutations.SET_USER_SUBSCRIPTIONS, { subscriptions });
        Vue.toasted.success(`Resumed successfully`);
        return true;
    } catch (error) {
        console.error(_get(error, 'response'));
        Vue.toasted.error(`Unable to resume subscription: ${_get(error, 'response.data.message', 'internal error')}`);
        return false;
    } finally {
        commit(mutations.LOADING, { loading: false });
    }
};

const cancelUserSubscription = async ({ commit }: ActionState, subscriptionId: string): Promise<boolean> => {
    commit(mutations.LOADING, { loading: true });
    try {
        await api.cancelSubscription(subscriptionId);
        const subscriptions = await api.loadUserCurrentSubscriptions();
        commit(mutations.SET_USER_SUBSCRIPTIONS, { subscriptions });
        Vue.toasted.success(`Canceled successfully`);
        return true;
    } catch (error) {
        console.error(_get(error, 'response'));
        Vue.toasted.error(`Unable to cancel subscription: ${_get(error, 'response.data.message', 'internal error')}`);
        return false;
    } finally {
        commit(mutations.LOADING, { loading: false });
    }
};

export default {
    [actions.LOGIN]: login,
    [actions.SIGNUP]: signup,
    [actions.RESET_PASSWORD]: resetPassword,
    [actions.RESEND_VERIFICATION_EMAIL]: resendVerificationEmail,
    [actions.SET_NEW_PASSWORD]: setNewPassword,
    [actions.VERIFY_EMAIL]: verifyEmail,
    [actions.LOGOUT]: logout,
    [actions.CHECK_AUTH_TOKEN]: checkAuthToken,
    [actions.LOAD_DEVICE]: loadDevice,
    [actions.LOAD_DEVICES]: loadDevices,
    [actions.UPDATE_DEVICE]: updateDevice,
    [actions.UPDATE_PASSWORD]: updatePassword,
    [actions.UPDATE_EMAIL]: updateEmail,
    [actions.UPDATE_USER]: updateUser,
    [actions.PAUSE_USER_SUBSCRIPTION]: pauseUserSubscription,
    [actions.RESUME_USER_SUBSCRIPTION]: resumeUserSubscription,
    [actions.CANCEL_USER_SUBSCRIPTION]: cancelUserSubscription,
    [actions.LOAD_GROUPS]: loadGroups,
};
